import React, { useRef, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { X, Check, RotateCcw, Pen } from 'lucide-react';
import './SignatureCapture.css';

/**
 * Composant réutilisable de capture de signature avec horodatage
 * Utilisé pour : PV d'AG, VPC, Feuille de présence
 */
const SignatureCapture = ({
  onSign,
  onCancel,
  signerName = '',
  signerRole = '',
  title = 'Signature',
  subtitle = '',
  disabled = false,
  showTimestamp = true,
  className = ''
}) => {
  const sigCanvas = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [isSigning, setIsSigning] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatDateTime = (date) => {
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatTimestamp = (date) => {
    return date.toISOString();
  };

  const handleClear = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setIsEmpty(true);
    }
  };

  const handleEnd = () => {
    if (sigCanvas.current) {
      setIsEmpty(sigCanvas.current.isEmpty());
    }
  };

  const handleSign = async () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      setIsSigning(true);
      
      const signatureData = sigCanvas.current.toDataURL('image/png');
      const timestamp = new Date();
      
      try {
        await onSign({
          signature: signatureData,
          timestamp: formatTimestamp(timestamp),
          timestampFormatted: formatDateTime(timestamp),
          signerName,
          signerRole
        });
      } catch (error) {
        console.error('Erreur lors de la signature:', error);
      } finally {
        setIsSigning(false);
      }
    }
  };

  return (
    <div className={`signature-capture ${className}`} data-testid="signature-capture">
      {/* Header */}
      <div className="signature-header">
        <div className="signature-title-section">
          <h3 className="signature-title">{title}</h3>
          {subtitle && <p className="signature-subtitle">{subtitle}</p>}
        </div>
        {onCancel && (
          <button 
            onClick={onCancel} 
            className="signature-close-btn"
            data-testid="signature-cancel-btn"
          >
            <X size={20} />
          </button>
        )}
      </div>

      {/* Signer Info */}
      {(signerName || signerRole) && (
        <div className="signer-info">
          <Pen size={16} className="text-slate-400" />
          <div>
            {signerName && <span className="signer-name">{signerName}</span>}
            {signerRole && <span className="signer-role">{signerRole}</span>}
          </div>
        </div>
      )}

      {/* Timestamp display */}
      {showTimestamp && (
        <div className="timestamp-display" data-testid="signature-timestamp">
          <span className="timestamp-label">Horodatage :</span>
          <span className="timestamp-value">{formatDateTime(currentTime)}</span>
        </div>
      )}

      {/* Canvas */}
      <div className="signature-canvas-container">
        <SignatureCanvas
          ref={sigCanvas}
          canvasProps={{
            className: 'signature-canvas',
            'data-testid': 'signature-canvas'
          }}
          backgroundColor="white"
          penColor="#1e293b"
          onEnd={handleEnd}
        />
        {isEmpty && (
          <div className="signature-placeholder">
            <Pen size={24} className="text-slate-300" />
            <span>Signez ici avec votre souris ou votre doigt</span>
          </div>
        )}
      </div>

      {/* Instructions */}
      <p className="signature-instructions">
        En signant, vous certifiez l'exactitude des informations et acceptez que cette signature électronique 
        soit horodatée et intégrée au document officiel.
      </p>

      {/* Actions */}
      <div className="signature-actions">
        <button
          onClick={handleClear}
          className="signature-btn signature-btn-secondary"
          disabled={isEmpty || disabled || isSigning}
          data-testid="signature-clear-btn"
        >
          <RotateCcw size={18} />
          <span>Effacer</span>
        </button>
        <button
          onClick={handleSign}
          className="signature-btn signature-btn-primary"
          disabled={isEmpty || disabled || isSigning}
          data-testid="signature-confirm-btn"
        >
          {isSigning ? (
            <>
              <span className="animate-spin">⏳</span>
              <span>Signature en cours...</span>
            </>
          ) : (
            <>
              <Check size={18} />
              <span>Valider la signature</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default SignatureCapture;
